import { Check, X } from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// PasswordStrength — UX indicator for any password input.
//
// Renders:
//   • 4 segmented bars filling left→right as the password meets criteria
//   • A label (Weak / Fair / Good / Strong)
//   • Inline checklist of unmet requirements (✓ when met, × when not)
//
// The criteria match the same regex used for validation server-side
// (see PASSWORD_PATTERN), so what's shown == what's enforced.
// ─────────────────────────────────────────────────────────────

interface PasswordStrengthProps {
  /** The current password value. Empty hides the meter entirely. */
  value: string;
  /** When true the meter & checklist always render (useful while the user is typing). */
  alwaysShow?: boolean;
}

interface Check {
  key: string;
  label: string;
  met: boolean;
}

function evaluate(pwd: string): { score: number; checks: Check[] } {
  const checks: Check[] = [
    { key: 'len',   label: '8+ chars',  met: pwd.length >= 8 },
    { key: 'upper', label: 'Uppercase', met: /[A-Z]/.test(pwd) },
    { key: 'lower', label: 'Lowercase', met: /[a-z]/.test(pwd) },
    { key: 'num',   label: 'Number',    met: /[0-9]/.test(pwd) },
    { key: 'sym',   label: 'Special',   met: /[$@$!%*?&]/.test(pwd) },
  ];
  return { score: checks.filter((c) => c.met).length, checks };
}

// Strength bucket → label + accent colour.
// Buckets: 0 hidden · 1 Weak · 2 Weak · 3 Fair · 4 Good · 5 Strong
const STRENGTH = [
  { label: '',        color: 'var(--color-border)',    bars: 0 },
  { label: 'Very weak', color: 'var(--color-bad)',     bars: 1 },
  { label: 'Weak',    color: 'var(--color-bad)',       bars: 1 },
  { label: 'Fair',    color: 'var(--color-warn)',      bars: 2 },
  { label: 'Good',    color: 'var(--color-info)',      bars: 3 },
  { label: 'Strong',  color: 'var(--color-good)',      bars: 4 },
] as const;

export function PasswordStrength({ value, alwaysShow = false }: PasswordStrengthProps) {
  if (!value && !alwaysShow) return null;

  const { score, checks } = evaluate(value || '');
  const meta = STRENGTH[score];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginTop: 8,
      }}
      aria-live="polite"
    >
      {/* Bars + label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', gap: 4, flex: 1 }}>
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 3,
                background: i < meta.bars ? meta.color : 'var(--color-border)',
                transition: 'background 180ms',
              }}
            />
          ))}
        </div>
        {value && (
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: meta.color,
              minWidth: 50,
              textAlign: 'right',
              fontFamily: 'var(--font-body)',
              letterSpacing: 0.2,
            }}
          >
            {meta.label}
          </span>
        )}
      </div>

      {/* Checklist (inline, wrapping) */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 12px',
          fontSize: 11.5,
          fontFamily: 'var(--font-body)',
        }}
      >
        {checks.map((c) => (
          <span
            key={c.key}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: c.met ? 'var(--color-good)' : 'var(--color-text-dim)',
              transition: 'color 180ms',
            }}
          >
            {c.met ? <Check size={11} strokeWidth={3} /> : <X size={11} strokeWidth={2.5} />}
            {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}
